import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Chip, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import InventoryIcon   from "@mui/icons-material/Inventory2Outlined";
import AssignmentIcon  from "@mui/icons-material/AssignmentOutlined";
import PendingIcon     from "@mui/icons-material/HourglassEmptyOutlined";
import WalletIcon      from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowUpIcon     from "@mui/icons-material/ArrowUpward";
import ArrowDownIcon   from "@mui/icons-material/ArrowDownward";
import SwapHorizIcon   from "@mui/icons-material/SwapHoriz";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import CheckIcon       from "@mui/icons-material/CheckCircleOutlined";
import ClockIcon       from "@mui/icons-material/AccessTimeOutlined";
import CancelIcon      from "@mui/icons-material/CancelOutlined";
import api from "../../services/api";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRupiah(v) {
  if (!v && v !== 0) return "Rp 0";
  const n = parseFloat(v);
  if (n >= 1_000_000_000) return `Rp ${(n / 1_000_000_000).toFixed(1)}M`;
  if (n >= 1_000_000)     return `Rp ${(n / 1_000_000).toFixed(1)}Jt`;
  if (n >= 1_000)         return `Rp ${(n / 1_000).toFixed(0)}Rb`;
  return `Rp ${n.toLocaleString("id-ID")}`;
}

const STATUS_CFG = {
  disetujui: { label: "Disetujui", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  approved:  { label: "Disetujui", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  pending:   { label: "Pending",   color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  ditolak:   { label: "Ditolak",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  rejected:  { label: "Ditolak",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

function getStatusCfg(s) {
  if (!s) return { label: "-", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  return STATUS_CFG[s.toLowerCase()] || { label: s, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

function StatusBadge({ statusName }) {
  const sc = getStatusCfg(statusName);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: "7px", py: "2px", borderRadius: "5px",
      backgroundColor: sc.bg, border: `1px solid ${sc.border}`,
    }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: sc.color }}>
        {sc.label}
      </Typography>
    </Box>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, badge, up, accent, loading }) {
  return (
    <Box sx={{
      backgroundColor: "#fff",
      borderRadius: "14px",
      border: "1px solid #e5e7eb",
      p: 2.5,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.07)", transform: "translateY(-1px)" },
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "10px",
          backgroundColor: accent + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent,
        }}>
          {icon}
        </Box>
        {!loading && badge && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.3,
            backgroundColor: up ? "#f0fdf4" : "#fef2f2",
            borderRadius: "20px", px: 0.9, py: 0.3,
          }}>
            {up
              ? <ArrowUpIcon sx={{ fontSize: 10, color: "#16a34a" }} />
              : <ArrowDownIcon sx={{ fontSize: 10, color: "#dc2626" }} />}
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: up ? "#16a34a" : "#dc2626" }}>
              {badge}
            </Typography>
          </Box>
        )}
      </Box>

      {loading ? (
        <>
          <Skeleton width="55%" height={34} />
          <Skeleton width="70%" height={14} />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.04em", lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>
            {label}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getCount = (key) => {
    if (!data) return 0;
    const f = data.status_pengajuan.find((x) => x.status?.toLowerCase() === key);
    return f ? f.total : 0;
  };

  const statCards = data ? [
    {
      label:  "Total Barang",
      value:  data.stat_cards.total_barang.value.toLocaleString("id-ID"),
      badge:  `+${data.stat_cards.total_barang.tambahan} bln ini`,
      up:     data.stat_cards.total_barang.tambahan >= 0,
      icon:   <InventoryIcon sx={{ fontSize: 19 }} />,
      accent: "#2563eb",
    },
    {
      label:  "Pengajuan Bulan Ini",
      value:  data.stat_cards.pengajuan_masuk.value.toLocaleString("id-ID"),
      badge:  `${data.stat_cards.pengajuan_masuk.bulan_lalu} bln lalu`,
      up:     data.stat_cards.pengajuan_masuk.value >= data.stat_cards.pengajuan_masuk.bulan_lalu,
      icon:   <AssignmentIcon sx={{ fontSize: 19 }} />,
      accent: "#059669",
    },
    {
      label:  "Menunggu Persetujuan",
      value:  data.stat_cards.menunggu_persetujuan.value.toLocaleString("id-ID"),
      badge:  data.stat_cards.menunggu_persetujuan.value > 0 ? "Perlu aksi" : "Semua beres",
      up:     data.stat_cards.menunggu_persetujuan.value === 0,
      icon:   <PendingIcon sx={{ fontSize: 19 }} />,
      accent: "#d97706",
    },
    {
      label:  "Total Nilai Pengadaan",
      value:  formatRupiah(data.stat_cards.total_nilai_pengadaan.value),
      badge:  `${formatRupiah(data.stat_cards.total_nilai_pengadaan.bulan_ini)} bln ini`,
      up:     true,
      icon:   <WalletIcon sx={{ fontSize: 19 }} />,
      accent: "#7c3aed",
    },
  ] : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.03em", lineHeight: 1 }}>
          Dashboard
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af", mt: 0.4 }}>
          Ringkasan sistem pengadaan & inventaris
        </Typography>
      </Box>

      {/* Stat Cards — MUI v7: pakai size={{ xs, sm, lg }} bukan item xs sm lg */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {(loading ? [1, 2, 3, 4] : statCards).map((card, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...(loading ? { loading: true, icon: null } : { ...card, loading: false })} />
          </Grid>
        ))}
      </Grid>

      {/* Row 2 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>

        {/* Pengajuan Terbaru */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>
                Pengajuan Terbaru
              </Typography>
              <Typography
                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/procurement`)}
                sx={{ fontSize: "0.72rem", color: "#2563eb", cursor: "pointer", fontWeight: 600,
                  "&:hover": { textDecoration: "underline" } }}>
                Lihat semua →
              </Typography>
            </Box>

            {/* Table header */}
            {!isMobile && (
              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 0.7fr 0.9fr", gap: 1,
                pb: 1, mb: 0.5, borderBottom: "1px solid #f3f4f6" }}>
                {["No. Pengajuan", "Unit", "Total", "Status"].map((h) => (
                  <Typography key={h} sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af",
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {h}
                  </Typography>
                ))}
              </Box>
            )}

            {loading
              ? [1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} sx={{ py: 1.4, borderBottom: "1px solid #f9fafb" }}>
                    <Skeleton height={18} width="85%" />
                  </Box>
                ))
              : !data?.pengajuan_terbaru?.length
              ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <AssignmentIcon sx={{ fontSize: 32, color: "#e5e7eb", mb: 1 }} />
                  <Typography sx={{ color: "#9ca3af", fontSize: "0.8rem" }}>Belum ada pengajuan</Typography>
                </Box>
              )
              : data.pengajuan_terbaru.map((r, i) => {
                  const sc = getStatusCfg(r.status);
                  return (
                    <Box key={r.id}
                      onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/procurement/${r.id}`)}
                      sx={{
                        display: isMobile ? "flex" : "grid",
                        flexDirection: isMobile ? "column" : "row",
                        gridTemplateColumns: isMobile ? "none" : "2fr 1.2fr 0.7fr 0.9fr",
                        gap: 1, py: 1.4, alignItems: isMobile ? "flex-start" : "center",
                        borderBottom: i < data.pengajuan_terbaru.length - 1 ? "1px solid #f9fafb" : "none",
                        "&:hover": { backgroundColor: "#f9fafb" },
                        borderRadius: "8px", px: 0.5, mx: -0.5,
                        transition: "background 0.1s", cursor: "pointer",
                      }}>
                      <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {r.request_number}
                        </Typography>
                        {isMobile && <StatusBadge statusName={r.status} type="sarpras" />}
                      </Box>
                      {!isMobile && (
                        <>
                          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {r.unit || "-"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#374151", fontWeight: 600 }}>
                            {formatRupiah(r.total_amount)}
                          </Typography>
                          <Chip label={sc.label} size="small" sx={{
                            height: 20, fontSize: "0.63rem", fontWeight: 700,
                            backgroundColor: sc.bg, color: sc.color,
                            border: `1px solid ${sc.border}`,
                            borderRadius: "6px", "& .MuiChip-label": { px: "7px" },
                            width: "fit-content",
                          }} />
                        </>
                      )}
                      {isMobile && (
                        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                            {r.unit || "-"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#374151", fontWeight: 700 }}>
                            {formatRupiah(r.total_amount)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
          </Box>
        </Grid>

        {/* Status Summary */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
            <Box sx={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: 3 }}>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827", mb: 2 }}>
                Status Pengajuan
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                {[
                  { key: "disetujui", label: "Disetujui", icon: <CheckIcon sx={{ fontSize: 18 }} />, color: "#059669", bg: "#ecfdf5" },
                  { key: "pending",   label: "Pending",   icon: <ClockIcon sx={{ fontSize: 18 }} />,  color: "#d97706", bg: "#fffbeb" },
                  { key: "ditolak",   label: "Ditolak",   icon: <CancelIcon sx={{ fontSize: 18 }} />, color: "#dc2626", bg: "#fef2f2" },
                ].map((s) => (
                  <Box key={s.key} sx={{
                    flex: 1, backgroundColor: s.bg, borderRadius: "12px", p: 2,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
                  }}>
                    <Box sx={{ color: s.color }}>{s.icon}</Box>
                    <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: "#111827", lineHeight: 1 }}>
                      {loading ? <Skeleton width={24} /> : getCount(s.key)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", color: "#6b7280", fontWeight: 500 }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Pergerakan Barang */}
      <Box sx={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>
            Pergerakan Barang Terbaru
          </Typography>
          <Typography
            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items`)}
            sx={{ fontSize: "0.72rem", color: "#2563eb", cursor: "pointer", fontWeight: 600,
              "&:hover": { textDecoration: "underline" } }}>
            Lihat semua →
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6 }}>
                <Skeleton variant="rectangular" height={68} sx={{ borderRadius: "10px" }} />
              </Grid>
            ))}
          </Grid>
        ) : !data?.pergerakan_terbaru?.length ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <MoveToInboxIcon sx={{ fontSize: 32, color: "#e5e7eb", mb: 1 }} />
            <Typography sx={{ color: "#9ca3af", fontSize: "0.8rem" }}>Belum ada pergerakan</Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {data.pergerakan_terbaru.map((m) => (
              <Grid key={m.id} size={{ xs: 12, sm: 6 }}>
                <Box sx={{
                  display: "flex", gap: 1.5, alignItems: "center",
                  p: 1.8, borderRadius: "10px", border: "1px solid #f3f4f6",
                  "&:hover": { borderColor: "#e5e7eb", backgroundColor: "#f9fafb" },
                  transition: "all 0.15s",
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "9px",
                    backgroundColor: "#eff6ff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#2563eb", flexShrink: 0,
                  }}>
                    <MoveToInboxIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {m.item_name || "-"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2 }}>
                      <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 72 }}>
                        {m.from_room || "-"}
                      </Typography>
                      <SwapHorizIcon sx={{ fontSize: 11, color: "#d1d5db", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.68rem", color: "#2563eb", fontWeight: 600,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 72 }}>
                        {m.to_room || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: "0.63rem", color: "#9ca3af", whiteSpace: "nowrap" }}>
                      {m.moved_at}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: "#6b7280", fontWeight: 500, mt: 0.2 }}>
                      {m.moved_by || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
